import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Stack, Box, Typography, Divider, Button } from '@mui/material'
import {
  Fingerprint as FingerprintIcon,
  BorderColor as BorderColorIcon,
  CameraAlt as CameraAltIcon,
} from '@mui/icons-material'

import { useUpdateCustomerMutation, useCreateTexasCashCardMutation, useGetTexasCashCardQuery } from 'services/customers'
import { useGetOrgImageQuery } from 'services/assets'

import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import CapturePhotoModal from 'components/generic/modals/dialog-modal/CapturePhotoModal'
import CaptureFingerprintModal from 'components/generic/modals/dialog-modal/CaptureFingerprintModal'
import CaptureSignatureModal from 'components/generic/modals/dialog-modal/CaptureSignatureModal'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalCustomActionBar from 'components/generic/modals/form-modal/FormModalCustomActionBar'
import DatePicker from 'components/generic/form/DatePicker'
import Loading from 'components/generic/Loading'

import {
  formatData as formatCustomerData,
  getDefaultValues,
  formatCustomer,
} from 'components/containers/customers/new-customer/utils'
import { mapContactToForm, formatFormData } from 'components/containers/inbound-tickets/cash-cards/utils'
import { ID_TYPES } from 'utils/constants'
import { getStatesAndProvinces, dispatchSnackbar } from 'utils/helpers'
import { formOptions } from 'utils/validation/cashCard'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

const GENDERS = [
  { id: 'M', name: 'Male', isHeader: false },
  { id: 'F', name: 'Female', isHeader: false },
]

const ID_TYPES_ARRAY = [ID_TYPES.LICENSE]

const CashCardModal = ({ onFormStateChange, onHideModal }) => {
  const dispatch = useDispatch()

  const { customerData } = useSelector(state => state.ui.formModalProps)
  const firstContact = customerData?.contacts?.[0]

  const { control, handleSubmit, setValue, formState, reset } = useForm(formOptions)
  const { errors, dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length

  const [openCapturePhotoModal, setOpenCapturePhotoModal] = useState(false)
  const [photoImage, setPhotoImage] = useState({
    filename: '',
    isTemp: true,
  })
  const [openCaptureFingerprintModal, setOpenCaptureFingerprintModal] = useState(false)
  const [fingerprintImage, setFingerprintImage] = useState({
    filename: '',
    isTemp: true,
  })
  const [openCaptureSignatureModal, setOpenCaptureSignatureModal] = useState(false)
  const [signatureImage, setSignatureImage] = useState({
    filename: '',
    isTemp: true,
  })

  const { data: orgImage, isLoading: isLoadingOrgImage } = useGetOrgImageQuery(
    {
      filename: photoImage?.filename,
      isTemp: false,
    },
    { skip: !photoImage?.filename },
  )

  const [updateCustomer, { isLoading: isUpdatingCustomer }] = useUpdateCustomerMutation()

  const [createTexasCashCard, { isLoading: isCreatingTexasCashCard }] = useCreateTexasCashCardMutation()

  const { data: cashCardData, isLoading: isLoadingCashCardData } = useGetTexasCashCardQuery(
    { contactId: firstContact?.id },
    { refetchOnMountOrArgChange: true, skip: !firstContact?.id },
  )

  const isLoading = isUpdatingCustomer || isCreatingTexasCashCard || isLoadingCashCardData

  const statesAndProvinces = useMemo(() => getStatesAndProvinces(['United States', 'Canada']), [])

  const getButtons = () => {
    const buttonArray = [
      {
        id: 'submit-card-button',
        text: `Generate ${cashCardData ? 'New ' : ''}Cash Card`,
        type: 'submit',
        variant: 'contained',
      },
    ]
    if (firstContact?.texasCashCardId && !isDirty) {
      buttonArray.unshift({
        id: 'view-application-button',
        text: 'View Application',
        variant: 'outlined',
        onClick: () => window.open(cashCardData?.texasCashCardAppUrl, '_blank'),
      })
      buttonArray.unshift({
        id: 'view-card-button',
        text: 'View Cash Card',
        variant: 'outlined',
        onClick: () => window.open(cashCardData?.texasCashCardUrl, '_blank'),
      })
    }
    return buttonArray
  }

  useEffect(() => {
    if (customerData) {
      mapContactToForm({
        contactData: firstContact || {},
        reset,
        setPhotoImage,
        setFingerprintImage,
        setSignatureImage,
      })
    }
  }, [customerData])

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const onSubmit = async data => {
    try {
      // first use formatCustomer to format the customer data from the GET request
      // then use formatData to reformat the data for the PUT request
      const formattedCustomerData = formatCustomerData(formatCustomer(getDefaultValues(), customerData))
      const currentContacts = customerData?.contacts || []
      const newContact = formatFormData(data, currentContacts?.[0] || {}, photoImage, fingerprintImage, signatureImage)
      if (!newContact?.image) {
        throw new Error('A photo is required')
      }
      if (!newContact?.thumbprintImage) {
        throw new Error('A fingerprint is required')
      }
      if (!newContact?.signatureImage) {
        throw new Error('A signature is required')
      }

      const { data: updatedCustomer, error: updatedCustomerError } = await updateCustomer({
        customer: {
          ...formattedCustomerData,
          contacts: [newContact, ...currentContacts.slice(1)],
        },
        customerId: customerData.id,
      })
      if (updatedCustomerError) throw new Error(updatedCustomerError?.data?.error)

      const updatedFirstContact = updatedCustomer?.contacts?.[0] || {}
      mapContactToForm({
        contactData: updatedFirstContact,
        reset,
        setPhotoImage,
        setFingerprintImage,
        setSignatureImage,
      })
      onFormStateChange(false)

      const { data: newCashCardData, error: createCashCardError } = await createTexasCashCard({
        texasCashCardRequestDto: {
          contactId: updatedFirstContact?.id,
        },
        contactId: updatedFirstContact?.id,
        customerId: customerData?.id,
      })
      if (createCashCardError) throw new Error(createCashCardError?.data?.error)

      window.open(newCashCardData?.texasCashCardUrl, '_blank')
      window.open(newCashCardData?.texasCashCardAppUrl, '_blank')
      dispatchSnackbar(dispatch, 'success', 'Cash card created successfully.')
      onHideModal()
    } catch (error) {
      const errorMessage = error?.data?.error || error?.message || 'An error occurred while creating the cash card.'
      dispatchSnackbar(dispatch, 'error', errorMessage)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isLoading && <Loading />}
      <Stack height="100vh">
        <FormModalHeading
          title={
            firstContact?.texasCashCardId
              ? `Cash Card (Expires ${dateFormatter(firstContact?.texasCashCardExpirationDate)})`
              : 'Create Cash Card'
          }
          backButtonPosition="right"
        />

        <Box flexGrow={1} overflow="auto" px={4}>
          <Typography variant="h6" color="textSecondary" mb={0} pb={0}>
            Applicant Information
          </Typography>

          <Box mt={1} mb={3}>
            <Divider />
          </Box>

          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Input
                name="firstName"
                label="First Name"
                defaultValue=""
                control={control}
                error={!!errors?.firstName}
                helperText={errors?.firstName?.message}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Input
                name="lastName"
                label="Last Name"
                defaultValue=""
                control={control}
                error={!!errors?.lastName}
                helperText={errors?.lastName?.message}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Select
                name="gender"
                label="Gender"
                variant="outlined"
                defaultValue=""
                control={control}
                error={!!errors?.gender}
                helperText={errors?.gender?.message}
                items={GENDERS}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <DatePicker
                name="dateOfBirth"
                label="Date of Birth"
                variant="outlined"
                control={control}
                error={!!errors?.dateOfBirth}
                helperText={errors?.dateOfBirth?.message}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Input
                name="licenseNumber"
                label="License Number"
                defaultValue=""
                control={control}
                error={!!errors?.licenseNumber}
                helperText={errors?.licenseNumber?.message}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Select
                name="licenseState"
                label="License State"
                variant="outlined"
                defaultValue=""
                control={control}
                error={!!errors?.licenseState}
                helperText={errors?.licenseState?.message}
                items={statesAndProvinces}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Select
                name="licenseType"
                label="License Type"
                variant="outlined"
                defaultValue=""
                control={control}
                error={!!errors?.licenseType}
                helperText={errors?.licenseType?.message}
                items={ID_TYPES_ARRAY}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Input
                name="address1"
                label="Address 1"
                defaultValue=""
                control={control}
                error={!!errors?.address1}
                helperText={errors?.address1?.message}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Input
                name="address2"
                label="Address 2"
                defaultValue=""
                control={control}
                error={!!errors?.address2}
                helperText={errors?.address2?.message}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Input
                name="phoneNumber"
                label="Phone Number"
                defaultValue=""
                control={control}
                error={!!errors?.phoneNumber}
                helperText={errors?.phoneNumber?.message}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Input
                name="city"
                label="City"
                defaultValue=""
                control={control}
                error={!!errors?.city}
                helperText={errors?.city?.message}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Select
                name="state"
                label="State"
                variant="outlined"
                defaultValue=""
                control={control}
                error={!!errors?.state}
                helperText={errors?.state?.message}
                items={statesAndProvinces}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Input
                name="zip"
                label="Zip"
                defaultValue=""
                control={control}
                error={!!errors?.zip}
                helperText={errors?.zip?.message}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                sx={{
                  width: '100%',
                }}
                variant="outlined"
                startIcon={<CameraAltIcon />}
                onClick={() => setOpenCapturePhotoModal(true)}
              >
                {photoImage.filename ? 'View Photo' : 'Take A Photo'}
              </Button>
              <Button
                sx={{
                  width: '100%',
                }}
                variant="outlined"
                startIcon={<FingerprintIcon />}
                onClick={() => setOpenCaptureFingerprintModal(true)}
              >
                {fingerprintImage.filename ? 'View Fingerprint' : 'Capture Fingerprint'}
              </Button>
              <Button
                sx={{
                  width: '100%',
                }}
                variant="outlined"
                startIcon={<BorderColorIcon />}
                onClick={() => setOpenCaptureSignatureModal(true)}
              >
                {signatureImage.filename ? 'View Signature' : 'Capture Signature'}
              </Button>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Box width="100%">
                {isLoadingOrgImage && <Loading localized />}
                {orgImage && (
                  <img
                    src={orgImage}
                    alt="Cash Card Face Pic"
                    style={{
                      width: '100%',
                      objectFit: 'contain',
                      opacity: orgImage && !isLoadingOrgImage ? 1 : 0,
                    }}
                  />
                )}
              </Box>
              <Box width="100%" />
              <Box width="100%" />
            </Stack>
          </Stack>
        </Box>

        {openCapturePhotoModal && (
          <CapturePhotoModal
            title="Take a Photo"
            hideDividers
            open={openCapturePhotoModal}
            onSave={filename => {
              setValue('photoImageFileName', filename)
              setPhotoImage({
                filename,
                isTemp: true,
              })
            }}
            onClose={() => setOpenCapturePhotoModal(false)}
            photoImage={photoImage}
            dialogStyles={{
              '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
            }}
          />
        )}

        {openCaptureFingerprintModal && (
          <CaptureFingerprintModal
            title="Capture Fingerprint"
            hideDividers
            open={openCaptureFingerprintModal}
            onSave={filename => {
              setValue('fingerPrintImageFileName', filename)
              setFingerprintImage({
                filename,
                isTemp: true,
              })
            }}
            onClose={() => setOpenCaptureFingerprintModal(false)}
            fingerprintImage={fingerprintImage}
            dialogStyles={{
              '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
            }}
          />
        )}

        {openCaptureSignatureModal && (
          <CaptureSignatureModal
            title="Capture Signature"
            hideDividers
            open={openCaptureSignatureModal}
            onSave={filename => {
              setValue('signatureImageFileName', filename)
              setSignatureImage({
                filename,
                isTemp: true,
              })
            }}
            onClose={() => setOpenCaptureSignatureModal(false)}
            signatureImage={signatureImage}
            dialogStyles={{
              '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
            }}
          />
        )}

        <FormModalCustomActionBar buttonConfigs={getButtons()} justify="flex-start" />
      </Stack>
    </form>
  )
}

CashCardModal.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

CashCardModal.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default CashCardModal
